import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  ColorInput,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBuilding,
  IconCamera,
  IconUsers,
  IconFileText,
  IconMapPin,
  IconPalette,
  IconLayoutGrid,
  IconTrash,
  IconDeviceFloppy,
  IconPlus,
  IconUpload,
  IconDownload,
  IconX,
  IconCurrencyDollar,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { usePermissions } from '../../../contexts/PermissionContext';
import { canNavigateToUserProfilePage, providerUserProfilePath } from '../../../lib/userProfileRouteAccess';
import { swapPracticeOwner } from '../../../lib/adminActionsService';
import { attachmentDataUrl, createSecuredAttachment } from '../../../lib/createSecuredAttachment';
import { PortalTab } from './PortalTab';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import {
  Practice,
  PracticeLocation,
  PortalTile,
  User,
} from '../../users/types/rbac';
import {
  fetchLegalDocuments,
  uploadLegalDocument,
  deleteLegalDocument,
  downloadLegalDocument,
  type LegalDocumentView,
} from '../../../lib/legalDocumentService';
import {
  legalDocTypeCodes,
  legalDocTypeLabels,
  requiredLegalDocTypes,
  type LegalDocTypeCode,
} from '@/schemas/legal-document.schema';
import { PracticeProfileContent } from './PracticeProfileContent';
import { LocationTileMap } from './LocationTileMap';
import { EquipmentManagementSection } from './EquipmentManagementSection';
import { RoomManagementSection } from './RoomManagementSection';
import { AddressSearchInput } from './AddressSearchInput';
import { UsersPage } from '../../users/UsersPage';
import { StaffAddClientModal } from '../../users/components/StaffAddClientModal';
import { CreateUserModal } from '../../users/components/CreateUserModal';
import { useLocationResources } from '../../../hooks/useLocationResources';
import { FEATURE_VISIBILITY } from '../../../config/featureVisibility';
import { isUnifyPlatformStaffRole } from '@unify/fhir-model/staff-role-org';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import {
  getDefaultSiteWeeklyHours,
  locationHasWeeklyHours,
  type PracticeRoomLocationInput,
  type PracticeSiteLocationInput,
} from '@unify/fhir-model/resources/location';
import { practiceSiteSchema } from '@/schemas';
import { WeeklyAvailabilityEditor } from '@/features/calendar/WeeklyAvailabilityEditor';
import { getProviderWeeklyAvailabilityUiDisableReason } from '@/features/calendar/providerAvailabilityValidation';
import {
  formatWeeklyHoursSummary,
  uiToWeeklyHours,
  weeklyHoursToUi,
} from '@/lib/locationWeeklyHoursUi';
import { US_STATES as US_STATE_OPTIONS } from '../../onboarding/constants';
import { useResolvedImageUrl } from '../../../hooks/useResolvedImageUrl';

interface PracticeSettingsProps {
  practice: Practice;
  /** Directory users from Medplum (same source as Practices & Users). Filtered by this practice. */
  directoryUsers: User[];
  onBack: () => void;
  onUpdatePractice: (id: string, updates: Partial<Practice>) => Promise<boolean> | void;
  onDeletePractice?: (id: string) => void;
  /** When true (e.g. from Users), show the practice name as the page title instead of "Practice Settings" */
  usePracticeNameAsTitle?: boolean;
  /** When provided (e.g. from Users), new/updated users are synced to platform state. */
  onUpdateUser?: (id: string, updates: Partial<User>) => void;
  onUserCreated?: (payload: import('../../../lib/adminDirectoryService').CreateUserPayload) => Promise<User>;
  /** Called when staff completes the Add Client intake wizard. */
  onClientAdded?: (user: User, context?: { organizationId: string }) => void;
  /** Called when the Add Client modal closes (refresh directory after success or dismiss). */
  onAddClientModalClosed?: () => void;
  /** When true, hide the back arrow (e.g. when scoped to current user's practice from Settings). */
  hideBackButton?: boolean;
  /** Initial tab to display (e.g. from ?tab= query param). */
  initialTab?: string;
  /** Directory users for this practice (Medplum admin directory); omit or pass [] when none. */
  practiceUsers?: User[];
}

const REQUIRED_LEGAL_DOCS = legalDocTypeCodes.map((code) => ({
  code,
  display: legalDocTypeLabels[code],
  required: requiredLegalDocTypes.includes(code),
}));

function getOperationOutcomeMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'outcome' in err) {
    const issue = (err as {
      outcome?: { issue?: Array<{ diagnostics?: string; details?: { text?: string } }> };
    }).outcome?.issue?.[0];
    if (issue?.diagnostics) return issue.diagnostics;
    if (issue?.details?.text) return issue.details.text;
  }
  return err instanceof Error ? err.message : String(err);
}

export function PracticeSettings({
  practice,
  directoryUsers: _directoryUsers,
  onBack,
  onUpdatePractice,
  usePracticeNameAsTitle = false,
  onUpdateUser,
  onUserCreated,
  onClientAdded,
  onAddClientModalClosed,
  hideBackButton = false,
  initialTab,
  practiceUsers = [],
}: PracticeSettingsProps) {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const sessionProfile = useMedplumProfile();
  const { role: sessionRole, permissions } = usePermissions();
  const canEditSettings = permissions?.can_edit_settings !== false;
  const [, setSearchParams] = useSearchParams();
  const locationResources = useLocationResources({
    organizationId: practice.id,
    organizationDisplay: practice.name,
  });
  const [editedPractice, setEditedPractice] = useState(practice);
  const [hasChanges, setHasChanges] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);
  const [brandDirty, setBrandDirty] = useState(false);
  const [portalDirty, setPortalDirty] = useState(false);
  const [activeTab, _setActiveTab] = useState<string | null>(
    initialTab && initialTab !== 'integration' ? initialTab : 'profile',
  );
  const [incomingOwnerId, setIncomingOwnerId] = useState<string | null>(null);
  const [ownershipSwapLoading, setOwnershipSwapLoading] = useState(false);
  const canTransferOwnership =
    sessionRole === 'unify-admin' && permissions?.edit_users === true;
  const canInvitePractitioners =
    isUnifyPlatformStaffRole(sessionRole) && permissions?.edit_users === true;

  const ownershipCandidates = useMemo(
    () =>
      practiceUsers
        .filter(
          (u) =>
            u.id !== practice.ownerId
            && u.role !== 'client'
            && u.status !== 'Inactive',
        )
        .map((u) => ({
          value: u.id,
          label: `${u.name || u.email || u.id} (${u.role})`,
        })),
    [practiceUsers, practice.ownerId],
  );

  const setActiveTab = (tab: string | null) => {
    _setActiveTab(tab);
    if (tab) {
      setSearchParams({ tab }, { replace: true });
    }
  };

  const practiceTabVisible: Record<string, boolean> = {
    profile: true,
    users: FEATURE_VISIBILITY.practiceSettingsUsers,
    legal: FEATURE_VISIBILITY.practiceSettingsLegal,
    locations: true,
    branding: FEATURE_VISIBILITY.practiceSettingsBranding,
    portal: FEATURE_VISIBILITY.practiceSettingsPortal,
    finances: FEATURE_VISIBILITY.practiceSettingsFinances,
  };

  useEffect(() => {
    if (activeTab && practiceTabVisible[activeTab] === false) {
      setActiveTab('profile');
    }
  }, [activeTab]);
  const [legalDocuments, setLegalDocuments] = useState<LegalDocumentView[]>([]);
  const [legalDocsLoading, setLegalDocsLoading] = useState(true);
  const [legalDocsError, setLegalDocsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLegalDocsLoading(true);
    fetchLegalDocuments(medplum, practice.id)
      .then((docs) => { if (!cancelled) { setLegalDocuments(docs); setLegalDocsError(null); } })
      .catch((err) => { if (!cancelled) setLegalDocsError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setLegalDocsLoading(false); });
    return () => { cancelled = true; };
  }, [medplum, practice.id]);

  const [portalTiles, setPortalTiles] = useState<PortalTile[]>(
    practice.portalTiles || []
  );
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [locationFormError, setLocationFormError] = useState<string | null>(null);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newLocation, setNewLocation] = useState<Partial<PracticeLocation>>({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    hours: getDefaultSiteWeeklyHours(),
  });
  const [newDocument, setNewDocument] = useState<{ name: string; type: LegalDocTypeCode; expirationDate?: string }>({
    name: '',
    type: 'w9-form',
  });
  const [legalDocFile, setLegalDocFile] = useState<File | null>(null);
  const [legalDocUploading, setLegalDocUploading] = useState(false);
  const legalAddDocumentPanelRef = useRef<HTMLDivElement>(null);
  const [legalUploadPrompt, setLegalUploadPrompt] = useState<string | null>(null);
  const [pendingLegalScroll, setPendingLegalScroll] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(
    practice.branding?.primaryColor ?? '#212b36'
  );
  const [secondaryColor, setSecondaryColor] = useState(
    practice.branding?.secondaryColor ?? '#10b981'
  );

  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountType, setAccountType] = useState<string | null>('checking');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPrimaryColor(practice.branding?.primaryColor ?? '#212b36');
    setSecondaryColor(practice.branding?.secondaryColor ?? '#10b981');
  }, [practice.branding?.primaryColor, practice.branding?.secondaryColor]);

  useEffect(() => {
    if (hasChanges) return;
    setEditedPractice(practice);
    setPortalTiles(practice.portalTiles || []);
  }, [practice, hasChanges]);

  // Blur when location modal opens so map tiles/cards don't stay highlighted
  useEffect(() => {
    if (showAddLocation || editingLocationId) {
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }, [showAddLocation, editingLocationId]);

  // Deletion dialog states
  const [showDeleteLocationDialog, setShowDeleteLocationDialog] = useState(false);
  const [showDeleteDocumentDialog, setShowDeleteDocumentDialog] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);
  const [locationSaving, setLocationSaving] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoHover, setLogoHover] = useState(false);
  const resolvedLogoUrl = useResolvedImageUrl(editedPractice.branding?.logoUrl);

  const handleLogoClick = () => {
    logoInputRef.current?.click();
  };

  const [logoUploading, setLogoUploading] = useState(false);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    e.target.value = '';

    setLogoUploading(true);
    try {
      const attachment = await createSecuredAttachment(
        medplum,
        file,
        { reference: `Organization/${practice.id}` },
        file.name,
        file.type,
      );
      const binaryUrl = attachmentDataUrl(attachment);
      setEditedPractice((prev) => ({
        ...prev,
        branding: {
          ...prev.branding,
          logoUrl: binaryUrl,
          primaryColor: prev.branding?.primaryColor ?? primaryColor,
          secondaryColor: prev.branding?.secondaryColor ?? secondaryColor,
        },
      }));
      setBrandDirty(true);
      setHasChanges(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notifications.show({ title: 'Could not upload logo', message: msg, color: 'red' });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Partial<Practice> = {};

      if (profileDirty) {
        updates.name = editedPractice.name;
        updates.status = editedPractice.status;
        updates.email = editedPractice.email;
        updates.phone = editedPractice.phone;
        updates.websiteUrl = editedPractice.websiteUrl;
        updates.cancellationWindowHours = editedPractice.cancellationWindowHours;
        updates.emailResponseTimeframe = editedPractice.emailResponseTimeframe;
        updates.legalName = editedPractice.legalName;
        updates.taxId = editedPractice.taxId;
        updates.npi = editedPractice.npi;
        updates.legalEntityAddress = editedPractice.legalEntityAddress;
      }

      if (brandDirty) {
        updates.bio = editedPractice.bio;
        updates.branding = {
          logoUrl: editedPractice.branding?.logoUrl,
          primaryColor,
          secondaryColor,
        };
      }

      if (portalDirty) {
        updates.portalTiles = portalTiles;
      }

      await onUpdatePractice(practice.id, updates);
      setHasChanges(false);
      setProfileDirty(false);
      setBrandDirty(false);
      setPortalDirty(false);
      notifications.show({ title: 'Settings saved', message: 'Practice settings updated successfully.', color: 'green' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notifications.show({ title: 'Could not save settings', message: msg, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddLocation = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    setLocationFormError(null);
    setEditingLocationId(null);
    setNewLocation({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      hours: getDefaultSiteWeeklyHours(),
    });
    setShowAddLocation(true);
  };

  const handleOpenEditLocation = (location: PracticeLocation) => {
    (document.activeElement as HTMLElement | null)?.blur();
    setLocationFormError(null);
    setShowAddLocation(false);
    setEditingLocationId(location.id);
    setNewLocation({
      name: location.name,
      addressLine1: location.addressLine1,
      addressLine2: location.addressLine2 ?? '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode,
      phone: location.phone ?? '',
      latitude: location.latitude,
      longitude: location.longitude,
      status: location.status ?? 'active',
      hours: locationHasWeeklyHours(location.hours) ? location.hours : getDefaultSiteWeeklyHours(),
    });
  };

  const handleSaveLocation = async () => {
    setLocationFormError(null);
    const result = practiceSiteSchema.safeParse({
      name: newLocation.name,
      addressLine1: newLocation.addressLine1,
      addressLine2: newLocation.addressLine2 || undefined,
      city: newLocation.city,
      state: newLocation.state,
      zipCode: newLocation.zipCode,
      phone: newLocation.phone || undefined,
      latitude: newLocation.latitude,
      longitude: newLocation.longitude,
      status: newLocation.status ?? 'active',
    });
    if (!result.success) {
      const messages = result.error.issues.map((i) => i.message);
      setLocationFormError(`Please fix: ${messages.join(', ')}`);
      return;
    }
    const hours = newLocation.hours ?? {};
    if (!locationHasWeeklyHours(hours)) {
      setLocationFormError('Select at least one open day with start and end times');
      return;
    }
    const hoursReason = getProviderWeeklyAvailabilityUiDisableReason(weeklyHoursToUi(hours));
    if (hoursReason) {
      setLocationFormError(hoursReason);
      return;
    }
    setLocationSaving(true);
    const validated = result.data;
    const payload: PracticeSiteLocationInput = {
      name: validated.name,
      addressLine1: validated.addressLine1,
      addressLine2: validated.addressLine2 || undefined,
      city: validated.city,
      state: validated.state,
      zipCode: validated.zipCode,
      phone: validated.phone || undefined,
      latitude: validated.latitude,
      longitude: validated.longitude,
      status: validated.status as PracticeSiteLocationInput['status'],
      hours,
    };
    try {
      if (editingLocationId) {
        await locationResources.updateSite(editingLocationId, payload);
        setEditingLocationId(null);
      } else {
        await locationResources.createSite(payload);
        setShowAddLocation(false);
      }
      setNewLocation({
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLocationFormError(msg);
      notifications.show({ title: 'Could not save location', message: msg, color: 'red' });
    } finally {
      setLocationSaving(false);
    }
  };

  const handleCloseLocationForm = () => {
    setLocationFormError(null);
    setShowAddLocation(false);
    setEditingLocationId(null);
    setNewLocation({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      hours: getDefaultSiteWeeklyHours(),
    });
  };

  const handleDeleteLocation = (id: string) => {
    setLocationToDelete(id);
    setShowDeleteLocationDialog(true);
  };

  const closeLegalDocumentForm = () => {
    setShowAddDocument(false);
    setLegalDocFile(null);
    setLegalUploadPrompt(null);
    setPendingLegalScroll(false);
  };

  const openRequiredLegalDocumentUpload = (doc: { name: string; type: LegalDocTypeCode }) => {
    setNewDocument({ name: doc.name, type: doc.type });
    setLegalDocFile(null);
    setShowAddDocument(true);
    setLegalUploadPrompt(`Use the upload form below to add ${doc.name}.`);
    setPendingLegalScroll(true);
  };

  useEffect(() => {
    if (!showAddDocument || !pendingLegalScroll) return;
    const timer = window.setTimeout(() => {
      legalAddDocumentPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      setPendingLegalScroll(false);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [showAddDocument, pendingLegalScroll]);

  const handleAddDocument = async () => {
    if (!newDocument.name || !newDocument.type || !legalDocFile) return;
    setLegalDocUploading(true);
    try {
      const doc = await uploadLegalDocument(
        medplum,
        practice.id,
        legalDocFile,
        newDocument.type,
        newDocument.name,
        newDocument.expirationDate,
      );
      setLegalDocuments((prev) => [...prev, doc]);
      setNewDocument({ name: '', type: 'w9-form' });
      setLegalDocFile(null);
      setShowAddDocument(false);
      setLegalUploadPrompt(null);
      notifications.show({ title: 'Document uploaded', message: `${newDocument.name} uploaded.`, color: 'green' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notifications.show({ title: 'Upload failed', message: msg, color: 'red' });
    } finally {
      setLegalDocUploading(false);
    }
  };

  const handleDeleteDocument = (docId: string) => {
    setDocumentToDelete(docId);
    setShowDeleteDocumentDialog(true);
  };

  const handleDownloadDocument = (doc: LegalDocumentView) => {
    void (async () => {
      try {
        await downloadLegalDocument(medplum, doc);
      } catch (err) {
        notifications.show({
          title: 'Download failed',
          message: err instanceof Error ? err.message : 'Could not download this file.',
          color: 'red',
        });
      }
    })();
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="lg">
            {!hideBackButton && (
              <ActionIcon variant="subtle" onClick={onBack} size="lg">
                <IconArrowLeft size={24} />
              </ActionIcon>
            )}
            <div>
              <Title order={2}>
                {usePracticeNameAsTitle ? practice.name : 'Practice Settings'}
              </Title>
              <Text c="gray.6" size="sm">
                Manage practice information and settings
              </Text>
            </div>
          </Group>

          {canEditSettings && activeTab !== 'users' && activeTab !== 'legal' && activeTab !== 'locations' && (
            <Group gap="sm">
              <Button
                leftSection={<IconDeviceFloppy size={18} />}
                onClick={() => void handleSave()}
                disabled={!hasChanges || saving}
                loading={saving}
              >
                Save Changes
              </Button>
            </Group>
          )}
        </Group>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconBuilding size={16} />}>
              Profile
            </Tabs.Tab>
            {FEATURE_VISIBILITY.practiceSettingsUsers ? (
              <Tabs.Tab value="users" leftSection={<IconUsers size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Users
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            ) : null}
            {FEATURE_VISIBILITY.practiceSettingsLegal ? (
              <Tabs.Tab value="legal" leftSection={<IconFileText size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Legal
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            ) : null}
            <Tabs.Tab value="locations" leftSection={<IconMapPin size={16} />}>
              Locations
            </Tabs.Tab>
            {FEATURE_VISIBILITY.practiceSettingsBranding ? (
              <Tabs.Tab value="branding" leftSection={<IconPalette size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Branding
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            ) : null}
            {FEATURE_VISIBILITY.practiceSettingsPortal ? (
              <Tabs.Tab value="portal" leftSection={<IconLayoutGrid size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Portal
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            ) : null}
            {FEATURE_VISIBILITY.practiceSettingsFinances ? (
              <Tabs.Tab value="finances" leftSection={<IconCurrencyDollar size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Finances
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            ) : null}
          </Tabs.List>

          {/* Profile Tab – identity, branding, demographics */}
          <Tabs.Panel value="profile" pt="lg">
            <Stack gap="lg">
              <PracticeProfileContent
                practice={editedPractice}
                canEdit={canEditSettings}
                onProfileChange={(updates) => {
                  setEditedPractice((prev) => ({ ...prev, ...updates }));
                  setProfileDirty(true);
                  setHasChanges(true);
                }}
              />
              <Card withBorder>
                <Stack gap="md">
                  <Title order={4}>Practice ownership</Title>
                  <Text size="sm" c="dimmed">
                    Current owner: <strong>{practice.ownerName || practice.ownerId || '—'}</strong>
                  </Text>
                  {canTransferOwnership ? (
                    <>
                      <Text size="sm">
                        Transfer ownership in one bundled swap (exactly one practice-owner at a
                        time). The outgoing owner becomes a practice admin.
                      </Text>
                      <Select
                        label="Incoming practice owner"
                        placeholder="Select a practice member"
                        data={ownershipCandidates}
                        value={incomingOwnerId}
                        onChange={setIncomingOwnerId}
                        searchable
                        clearable
                        style={{ maxWidth: 420 }}
                      />
                      <Group>
                        <Button
                          loading={ownershipSwapLoading}
                          disabled={!incomingOwnerId}
                          onClick={async () => {
                            if (!incomingOwnerId) return;
                            setOwnershipSwapLoading(true);
                            try {
                              await swapPracticeOwner(medplum, {
                                orgId: practice.id,
                                outgoingOwnerId: practice.ownerId,
                                incomingOwnerId,
                                outgoingNewRole: 'practice-admin',
                              });
                              const incoming = practiceUsers.find((u) => u.id === incomingOwnerId);
                              const ownerPatch = {
                                ownerId: incomingOwnerId,
                                ownerName: incoming?.name || incoming?.email || incomingOwnerId,
                              };
                              setEditedPractice((prev) => ({
                                ...prev,
                                ...ownerPatch,
                              }));
                              await onUpdatePractice(practice.id, ownerPatch);
                              setIncomingOwnerId(null);
                              notifications.show({
                                title: 'Practice owner swapped',
                                message: 'Ownership transferred successfully.',
                                color: 'green',
                              });
                            } catch (err) {
                              notifications.show({
                                title: 'Ownership swap failed',
                                message: err instanceof Error ? err.message : String(err),
                                color: 'red',
                              });
                            } finally {
                              setOwnershipSwapLoading(false);
                            }
                          }}
                        >
                          Transfer ownership
                        </Button>
                      </Group>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Only a Unify Admin can transfer practice ownership.
                    </Text>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Users Tab — same layout as Users section All Users, scoped to this practice */}
          <Tabs.Panel value="users" pt="lg">
            <UsersPage
              users={practiceUsers}
              scopePracticeId={practice.id}
              scopePractice={practice}
              onViewUser={(user) => {
                if (user.role !== 'client' && !canNavigateToUserProfilePage(user.id, sessionProfile?.id, sessionRole)) {
                  notifications.show({
                    title: 'Access denied',
                    message: 'You can only open your own profile. Ask a practice or platform admin to manage other users.',
                    color: 'red',
                  });
                  return;
                }
                navigate(providerUserProfilePath(user.id, user.role, sessionProfile?.id));
              }}
              onUpdateUser={onUpdateUser ?? (() => { })}
              onCreateUser={() => setShowCreateUser(true)}
              onCreateClient={() => setShowAddClientModal(true)}
              hideCreateUserButton={!canInvitePractitioners}
              onResendInvite={
                permissions?.edit_users
                  ? async (email) => {
                      const { resendInvite } = await import('../../../lib/adminDirectoryService');
                      await resendInvite(medplum, email);
                    }
                  : undefined
              }
            />
            {showAddClientModal && (
              <StaffAddClientModal
                opened
                onClose={() => {
                  setShowAddClientModal(false);
                  onAddClientModalClosed?.();
                }}
                practice={practice}
                onCreated={(user, context) => {
                  onClientAdded?.(user, context);
                }}
              />
            )}
            {showCreateUser && canInvitePractitioners && onUserCreated && (
              <CreateUserModal
                onClose={() => setShowCreateUser(false)}
                onCreateUser={async (payload) => {
                  const user = await onUserCreated(payload);
                  setShowCreateUser(false);
                  return user;
                }}
                practices={[practice]}
                isPlatformView={false}
                currentPracticeId={practice.id}
              />
            )}
          </Tabs.Panel>

          {/* Legal Tab */}
          <Tabs.Panel value="legal" pt="lg">
            <Stack gap="lg">
            {legalDocsError && (
              <Alert color="red" title="Could not load legal documents">{legalDocsError}</Alert>
            )}
            {/* Required Legal Documents Tracking */}
            <Card withBorder>
              <Title order={4} mb="xs">Required Legal Documents</Title>
              <Text size="sm" c="gray.6" mb="md">
                Track required and optional documents for compliance
              </Text>
              {legalDocsLoading ? (
                <Center py="xl"><Loader size="sm" /></Center>
              ) : (
              <Stack gap="xs">
                {REQUIRED_LEGAL_DOCS.map((docType) => {
                  const uploaded = legalDocuments.find((d) => d.typeCode === docType.code);
                  return (
                    <Card key={docType.code} withBorder p="sm">
                      <Group justify="space-between">
                        <Group gap="md">
                          <ThemeIcon size="lg" radius="md" variant="light" color={uploaded ? 'green' : 'gray'}>
                            <IconFileText size={20} />
                          </ThemeIcon>
                          <div>
                            <Group gap="xs">
                              <Text fw={500} size="sm">{docType.display}</Text>
                              {docType.required && (
                                <Badge size="xs" variant="light" color="red">Required</Badge>
                              )}
                            </Group>
                            {uploaded ? (
                              <Text size="xs" c="gray.6" mt={2}>
                                Uploaded: {uploaded.uploadedDate}{uploaded.name ? ` — ${uploaded.name}` : ''}
                              </Text>
                            ) : (
                              <Text size="xs" c="gray.6" mt={2}>Not yet uploaded</Text>
                            )}
                          </div>
                        </Group>
                        <Group gap="xs">
                          <Badge color={uploaded ? 'green' : 'gray'} variant="light">
                            {uploaded ? 'Uploaded' : 'Missing'}
                          </Badge>
                          {!uploaded && (
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconUpload size={14} />}
                              onClick={() => openRequiredLegalDocumentUpload({
                                name: docType.display,
                                type: docType.code,
                              })}
                            >
                              Upload
                            </Button>
                          )}
                          {uploaded && (
                            <ActionIcon variant="subtle" title="Download" onClick={() => handleDownloadDocument(uploaded)}>
                              <IconDownload size={16} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Group>
                    </Card>
                  );
                })}
              </Stack>
              )}
            </Card>

            <Card withBorder>
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={4}>Legal Documents</Title>
                  <Text size="sm" c="gray.6" mt="xs">
                    Manage tax forms, insurance certificates, and other legal
                    documentation
                  </Text>
                </div>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setShowAddDocument(true)}
                >
                  Add Document
                </Button>
              </Group>

              {/* Add Document Form */}
              {showAddDocument && (
                <Card
                  ref={legalAddDocumentPanelRef}
                  withBorder
                  mb="md"
                  p="lg"
                  style={
                    legalUploadPrompt
                      ? {
                          borderColor: 'var(--mantine-color-gold-5)',
                          boxShadow: '0 0 0 2px var(--mantine-color-gold-2)',
                        }
                      : undefined
                  }
                >
                  <Group justify="space-between" mb="md">
                    <Title order={5}>Add New Document</Title>
                    <ActionIcon variant="subtle" onClick={closeLegalDocumentForm}>
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>

                  {legalUploadPrompt && (
                    <Alert
                      color="gold"
                      icon={<IconUpload size={16} />}
                      mb="md"
                      withCloseButton
                      onClose={() => setLegalUploadPrompt(null)}
                    >
                      {legalUploadPrompt}
                    </Alert>
                  )}

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Document Name"
                        placeholder="e.g., W-9 Form 2024"
                        value={newDocument.name}
                        onChange={(e) =>
                          setNewDocument({ ...newDocument, name: e.target.value })
                        }
                        required
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Document Type"
                        data={legalDocTypeCodes.map((c) => ({ value: c, label: legalDocTypeLabels[c] }))}
                        value={newDocument.type}
                        onChange={(value) =>
                          setNewDocument({
                            ...newDocument,
                            type: (value as LegalDocTypeCode) || 'w9-form',
                          })
                        }
                        required
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Expiration Date (Optional)"
                        type="date"
                        value={newDocument.expirationDate || ''}
                        onChange={(e) =>
                          setNewDocument({
                            ...newDocument,
                            expirationDate: e.target.value || undefined,
                          })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Stack gap={4}>
                        <Text size="sm" fw={500}>File <Text span c="red">*</Text></Text>
                        <Button
                          variant="outline"
                          leftSection={<IconUpload size={16} />}
                          fullWidth
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf,.doc,.docx,.png,.jpg,.jpeg';
                            input.onchange = (ev) => {
                              const file = (ev.target as HTMLInputElement).files?.[0];
                              if (file) setLegalDocFile(file);
                            };
                            input.click();
                          }}
                        >
                          {legalDocFile ? legalDocFile.name : 'Choose File'}
                        </Button>
                      </Stack>
                    </Grid.Col>
                  </Grid>

                  <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={closeLegalDocumentForm}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => void handleAddDocument()}
                      loading={legalDocUploading}
                      disabled={!newDocument.name || !legalDocFile}
                    >
                      Upload Document
                    </Button>
                  </Group>
                </Card>
              )}

              {/* Documents List */}
              {legalDocsLoading ? (
                <Center py="xl"><Loader size="sm" /></Center>
              ) : legalDocuments.length === 0 ? (
                <Group justify="center" py="xl">
                  <Stack align="center" gap="lg">
                    <ThemeIcon size={64} radius="md" variant="light" color="gray">
                      <IconFileText size={32} />
                    </ThemeIcon>
                    <div>
                      <Text c="gray.6" size="sm" ta="center">
                        No legal documents uploaded yet
                      </Text>
                      <Text c="gray.6" size="xs" ta="center" mt="xs">
                        {`Click "Add Document" to get started`}
                      </Text>
                    </div>
                  </Stack>
                </Group>
              ) : (
                <Stack gap="xs">
                  {legalDocuments.map((doc) => (
                    <Card key={doc.id} withBorder p="lg">
                      <Group justify="space-between">
                        <Group gap="lg">
                          <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                            <IconFileText size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={500} size="sm">
                              {doc.name}
                            </Text>
                            <Group gap="lg" mt="xs">
                              <Text size="xs" c="gray.6">
                                {doc.typeDisplay}
                              </Text>
                              <Text size="xs" c="gray.6">
                                Uploaded: {doc.uploadedDate}
                              </Text>
                              {doc.expirationDate && (
                                <Text size="xs" c="gray.6">
                                  Expires: {doc.expirationDate}
                                </Text>
                              )}
                            </Group>
                          </div>
                        </Group>
                        <Group gap="xs">
                          {doc.binaryUrl && (
                            <ActionIcon variant="subtle" onClick={() => handleDownloadDocument(doc)} title="Download">
                              <IconDownload size={16} />
                            </ActionIcon>
                          )}
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </Card>
            </Stack>
          </Tabs.Panel>

          {/* Locations Tab */}
          <Tabs.Panel value="locations" pt="lg">
            <Card withBorder>
              <Box mb="md" style={{ position: 'relative', zIndex: 10 }}>
                <Group justify="space-between">
                  <div>
                    <Title order={4}>Practice Locations</Title>
                    <Text size="sm" c="gray.6" mt="xs">
                      Manage physical office locations for in-person sessions
                    </Text>
                  </div>
                  {canEditSettings && (
                    <Button
                      type="button"
                      leftSection={<IconPlus size={16} />}
                      onClick={handleOpenAddLocation}
                      aria-label="Add new location"
                    >
                      Add Location
                    </Button>
                  )}
                </Group>
              </Box>

              {locationResources.error && (
                <Alert color="red" title="Could not load locations" mb="md">
                  {locationResources.error}
                </Alert>
              )}

              {/* Add / Edit Location Modal */}
              <Modal
                opened={!!(showAddLocation || editingLocationId)}
                onClose={handleCloseLocationForm}
                title={editingLocationId ? 'Edit Location' : 'Add New Location'}
                size="xl"
              >
                <Stack gap="lg">
                  <TextInput
                    label="Location Name"
                    withAsterisk
                    placeholder="e.g., Downtown Office"
                    value={newLocation.name}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, name: e.target.value })
                    }
                  />

                  <AddressSearchInput
                    label="Street Address Line 1"
                    withAsterisk
                    placeholder="e.g., 123 Main St or search by address"
                    value={newLocation.addressLine1 ?? ''}
                    onChange={(v) => {
                      // Autocomplete can set value to full label "Street — City, State Zip"; keep only street part
                      const streetOnly = v.includes(' — ') ? v.split(' — ')[0].trim() : v;
                      setNewLocation((prev) => ({ ...prev, addressLine1: streetOnly }));
                    }}
                    onSelect={(suggestion) =>
                      setNewLocation((prev) => ({
                        ...prev,
                        addressLine1: suggestion.address,
                        city: suggestion.city,
                        state: suggestion.state,
                        zipCode: suggestion.zipCode,
                        latitude: suggestion.latitude,
                        longitude: suggestion.longitude,
                      }))
                    }
                    hideDescription
                  />

                  <TextInput
                    label="Street Address Line 2 (optional)"
                    placeholder="e.g., Suite 200"
                    value={newLocation.addressLine2 ?? ''}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, addressLine2: e.target.value })
                    }
                  />

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <TextInput
                        label="City"
                        withAsterisk
                        placeholder="e.g., San Francisco"
                        value={newLocation.city}
                        onChange={(e) =>
                          setNewLocation({ ...newLocation, city: e.target.value })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <Select
                        label="State"
                        withAsterisk
                        placeholder="Select state"
                        searchable
                        data={US_STATE_OPTIONS}
                        value={newLocation.state || null}
                        onChange={(v) =>
                          setNewLocation({ ...newLocation, state: v ?? '' })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <TextInput
                        label="Zip Code"
                        withAsterisk
                        placeholder="e.g., 94102"
                        value={newLocation.zipCode}
                        onChange={(e) =>
                          setNewLocation({ ...newLocation, zipCode: e.target.value })
                        }
                      />
                    </Grid.Col>
                  </Grid>

                  <TextInput
                    label="Phone Number (Optional)"
                    type="tel"
                    placeholder="e.g., (415) 555-0100"
                    value={newLocation.phone}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, phone: e.target.value })
                    }
                  />

                  <Select
                    label="Status"
                    data={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    value={newLocation.status ?? 'active'}
                    onChange={(v) =>
                      setNewLocation({ ...newLocation, status: (v as 'active' | 'inactive') ?? 'active' })
                    }
                  />

                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Hours
                    </Text>
                    <Text size="xs" c="gray.6">
                      Hours are in Mountain Time. Rooms inherit these hours unless they have custom hours.
                    </Text>
                    <WeeklyAvailabilityEditor
                      value={weeklyHoursToUi(newLocation.hours ?? getDefaultSiteWeeklyHours())}
                      onChange={(weekly) =>
                        setNewLocation({ ...newLocation, hours: uiToWeeklyHours(weekly) })
                      }
                    />
                  </Stack>

                  {locationFormError && (
                    <Alert
                      color="red"
                      title={
                        locationFormError.startsWith('Please fill in:')
                          ? 'Missing required fields'
                          : 'Could not save location'
                      }
                      withCloseButton
                      onClose={() => setLocationFormError(null)}
                    >
                      {locationFormError}
                    </Alert>
                  )}

                  <Group justify={editingLocationId ? 'space-between' : 'flex-end'} mt="md">
                    {editingLocationId && (
                      <Button
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          handleCloseLocationForm();
                          handleDeleteLocation(editingLocationId);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                    <Group gap="sm">
                      <Button variant="outline" onClick={handleCloseLocationForm}>
                        Cancel
                      </Button>
                      <Button loading={locationSaving} onClick={() => void handleSaveLocation()}>
                        {editingLocationId ? 'Save Changes' : 'Add Location'}
                      </Button>
                    </Group>
                  </Group>
                </Stack>
              </Modal>

              {/* Locations List */}
              {locationResources.loading && locationResources.sites.length === 0 ? (
                <Center py="xl">
                  <Loader size="lg" />
                </Center>
              ) : locationResources.sites.length === 0 ? (
                <Group justify="center" py="xl">
                  <Stack align="center" gap="lg">
                    <ThemeIcon size={64} radius="md" variant="light" color="gray">
                      <IconMapPin size={32} />
                    </ThemeIcon>
                    <div>
                      <Text c="gray.6" size="sm" ta="center">
                        No locations added yet
                      </Text>
                      <Text c="gray.6" size="xs" ta="center" mt="xs">
                        {`Click "Add Location" to get started`}
                      </Text>
                    </div>
                  </Stack>
                </Group>
              ) : (
                <Stack>
                  {locationResources.sites.map((location) => (
                    <Card key={location.id} withBorder p="md">
                      <Group justify="space-between">
                        <Group gap="md">
                          <LocationTileMap location={location} size={160} />
                          <div>
                            <Text fw={500} size="sm">
                              {location.name}
                            </Text>
                            <Text size="xs" c="gray.6" mt="xs">
                              {location.addressLine1 ?? (location as { address?: string }).address ?? ''}
                              {location.addressLine2 ? `, ${location.addressLine2}` : ''}
                            </Text>
                            <Text size="xs" c="gray.6">
                              {location.city}, {location.state} {location.zipCode}
                            </Text>
                            {location.phone && (
                              <Text size="xs" c="gray.6" mt="xs">
                                {location.phone}
                              </Text>
                            )}
                            {formatWeeklyHoursSummary(location.hours ?? {}) ? (
                              <Text size="xs" c="gray.6" mt="xs">
                                {formatWeeklyHoursSummary(location.hours ?? {})}
                              </Text>
                            ) : (
                              <Text size="xs" c="red.7" mt="xs">
                                Set hours to enable room booking.
                              </Text>
                            )}
                          </div>
                        </Group>
                        {canEditSettings && (
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => handleOpenEditLocation(location)}
                            aria-label={`Edit ${location.name}`}
                          >
                            Edit
                          </Button>
                        )}
                      </Group>
                      <RoomManagementSection
                        rooms={locationResources.roomsBySiteId[location.id] ?? []}
                        siteHours={location.hours ?? {}}
                        fixedEquipmentByRoom={
                          (locationResources.equipmentBySiteId[location.id] ?? [])
                            .filter((e) => e.portability === 'fixed' && e.homeRoomId)
                            .reduce<{ roomId: string; equipmentNames: string[] }[]>((acc, e) => {
                              const existing = acc.find((x) => x.roomId === e.homeRoomId);
                              if (existing) existing.equipmentNames.push(e.name);
                              else acc.push({ roomId: e.homeRoomId!, equipmentNames: [e.name] });
                              return acc;
                            }, [])
                        }
                        canEdit={canEditSettings}
                        onSaveRoom={async (editingRoomId, data) => {
                          const input: PracticeRoomLocationInput = {
                            name: data.name,
                            roomType: data.type,
                            capacity: data.capacity,
                            adaAccessible: data.adaAccessible,
                            status: data.status,
                            hours: data.hours,
                          };
                          if (editingRoomId) {
                            await locationResources.updateRoom(location.id, editingRoomId, input);
                          } else {
                            await locationResources.createRoom(location.id, input);
                          }
                        }}
                        onDeleteRoom={(roomId) => locationResources.deleteRoom(roomId)}
                        onToggleRoomStatus={async (roomId, active) => {
                          const rooms = locationResources.roomsBySiteId[location.id] ?? [];
                          const r = rooms.find((x) => x.id === roomId);
                          if (!r) return;
                          await locationResources.updateRoom(location.id, roomId, {
                            name: r.name,
                            roomType: r.type,
                            capacity: r.capacity,
                            adaAccessible: r.adaAccessible,
                            status: active ? 'active' : 'inactive',
                            hours: r.hours,
                          });
                        }}
                      />
                      <EquipmentManagementSection
                        therapyRooms={locationResources.roomsBySiteId[location.id] ?? []}
                        equipment={locationResources.equipmentBySiteId[location.id] ?? []}
                        canEdit={canEditSettings}
                        onSave={async (editingEqId, data) => {
                          if (editingEqId) {
                            await locationResources.updateEquipment(location.id, editingEqId, {
                              name: data.name,
                              portability: data.portability,
                              homeRoomId: data.homeRoomId,
                              status: data.status,
                            });
                          } else {
                            await locationResources.createEquipment(location.id, {
                              name: data.name,
                              portability: data.portability,
                              homeRoomId: data.homeRoomId,
                              status: data.status,
                            });
                          }
                        }}
                        onDelete={(equipmentId) => locationResources.deleteEquipment(equipmentId)}
                        onToggleStatus={async (equipmentId, active) => {
                          const items = locationResources.equipmentBySiteId[location.id] ?? [];
                          const eq = items.find((x) => x.id === equipmentId);
                          if (!eq) return;
                          await locationResources.updateEquipment(location.id, equipmentId, {
                            name: eq.name,
                            portability: eq.portability,
                            homeRoomId: eq.homeRoomId,
                            status: active ? 'active' : 'inactive',
                          });
                        }}
                      />
                    </Card>
                  ))}
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          {/* Branding Tab */}
          <Tabs.Panel value="branding" pt="lg">
            <Stack gap="lg">
              <Card withBorder>
                <Title order={4} mb="md">
                  Practice Logo
                </Title>
                <Text size="sm" c="gray.6" mb="md">
                  Upload a logo to represent your practice across the platform. Click the image to change it.
                </Text>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  style={{ display: 'none' }}
                  aria-hidden
                />
                <UnstyledButton
                  type="button"
                  onClick={handleLogoClick}
                  onMouseEnter={() => setLogoHover(true)}
                  onMouseLeave={() => setLogoHover(false)}
                  style={{
                    display: 'block',
                    width: 128,
                    height: 128,
                    flexShrink: 0,
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 'var(--mantine-radius-md)',
                    padding: 0,
                  }}
                  aria-label="Upload or change practice logo"
                >
                  <Box
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      borderRadius: 'var(--mantine-radius-md)',
                      overflow: 'hidden',
                      border: '2px solid var(--mantine-color-gray-3)',
                    }}
                  >
                    {editedPractice.branding?.logoUrl ? (
                      <Box
                        component="img"
                        src={resolvedLogoUrl}
                        alt="Practice logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Box
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'var(--mantine-color-gray-1)',
                        }}
                      >
                        <IconBuilding size={48} style={{ color: 'var(--mantine-color-gray-5)' }} />
                      </Box>
                    )}
                    <Box
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        opacity: logoHover ? 1 : 0,
                        transition: 'opacity 0.15s ease',
                      }}
                    >
                      <IconCamera size={28} stroke={1.5} />
                      <Text size="xs" fw={500}>
                        {editedPractice.branding?.logoUrl ? 'Change logo' : 'Upload logo'}
                      </Text>
                    </Box>
                  </Box>
                </UnstyledButton>
                {logoUploading && (
                  <Group gap="xs" mt="md">
                    <Loader size="xs" />
                    <Text size="xs" c="gray.6">Uploading logo…</Text>
                  </Group>
                )}
                <Text size="xs" c="gray.6" mt="md">
                  Recommended: square image, at least 256×256px, PNG or JPG
                </Text>
                <Stack
                  gap="xs"
                  pt="lg"
                  style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
                >
                  <Textarea
                    label="Practice Bio"
                    description="Describe your practice, services, and approach to care"
                    value={editedPractice.bio || ''}
                    onChange={(e) => {
                      setEditedPractice((prev) => ({ ...prev, bio: e.target.value }));
                      setBrandDirty(true);
                      setHasChanges(true);
                    }}
                    placeholder="Tell potential clients about your practice, your team's expertise, therapeutic approaches, and what makes your practice unique..."
                    minRows={6}
                  />
                  <Text size="xs" c="gray.6">
                    This bio will be displayed on your practice&apos;s public profile
                  </Text>
                </Stack>
              </Card>
              <Card withBorder>
                <Title order={4} mb="md">
                  Color Palette
                </Title>
                <Text size="sm" c="gray.6" mb="md">
                  Customize the primary and secondary colors for your practice&apos;s
                  branding
                </Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <ColorInput
                      label="Primary Color"
                      description="Used for buttons, headers, and primary UI elements"
                      value={primaryColor}
                      onChange={(value) => {
                        setPrimaryColor(value);
                        setBrandDirty(true);
                        setHasChanges(true);
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <ColorInput
                      label="Secondary Color"
                      description="Used for accents, highlights, and secondary UI elements"
                      value={secondaryColor}
                      onChange={(value) => {
                        setSecondaryColor(value);
                        setBrandDirty(true);
                        setHasChanges(true);
                      }}
                    />
                  </Grid.Col>
                </Grid>
                <Group
                  gap="lg"
                  mt="lg"
                  pt="lg"
                  style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
                >
                  <Text fw={500} size="sm">
                    Preview:
                  </Text>
                  <Button
                    style={{ backgroundColor: primaryColor }}
                    variant="filled"
                  >
                    Primary Button
                  </Button>
                  <Button
                    style={{ backgroundColor: secondaryColor }}
                    variant="filled"
                  >
                    Secondary Button
                  </Button>
                  <Button
                    variant="outline"
                    style={{ borderColor: primaryColor, color: primaryColor }}
                  >
                    Outlined Button
                  </Button>
                </Group>
              </Card>
              <Card
                withBorder
                style={{
                  borderLeft: `4px solid ${primaryColor}`,
                }}
              >
                <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb="xs">
                  Preview
                </Text>
                <Text size="sm" c="gray.6" mb="md">
                  How your branding appears in the client portal, emails, and
                  telehealth.
                </Text>
                <Group gap="lg">
                  <Avatar
                    size={64}
                    radius="md"
                    color="gray"
                    src={resolvedLogoUrl}
                  >
                    <IconBuilding size={32} />
                  </Avatar>
                  <Stack gap={4}>
                    <Text fw={500}>{editedPractice.name || 'Practice name'}</Text>
                    <Text size="sm" c="gray.6">
                      Client portal · Email · Telehealth
                    </Text>
                    <Group gap="xs" mt="xs">
                      <Button
                        size="xs"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Primary
                      </Button>
                      <Button
                        size="xs"
                        style={{ backgroundColor: secondaryColor }}
                      >
                        Secondary
                      </Button>
                    </Group>
                  </Stack>
                </Group>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Portal Tab */}
          <Tabs.Panel value="portal" pt="lg">
            <Stack gap="lg">
              <PortalTab
                medplum={medplum}
                organizationId={practice.id}
                tiles={portalTiles}
                onUpdateTiles={setPortalTiles}
                onMarkChanged={() => { setPortalDirty(true); setHasChanges(true); }}
              />
            </Stack>
          </Tabs.Panel>

          {/* Finances Tab */}
          <Tabs.Panel value="finances" pt="lg">
            <Card withBorder>
              <Stack gap="lg">
                <div>
                  <Title order={4}>Bank Account for Deposits</Title>
                  <Text size="sm" c="gray.6" mt="xs">
                    Payments and reimbursements will be deposited to this account
                  </Text>
                </div>
                <TextInput
                  label="Bank name"
                  placeholder="e.g., Chase, Bank of America"
                  value={bankName}
                  onChange={(e) => { setBankName(e.currentTarget.value); setHasChanges(true); }}
                />
                <TextInput
                  label="Account holder name"
                  placeholder="Name as it appears on the account"
                  value={accountHolderName}
                  onChange={(e) => { setAccountHolderName(e.currentTarget.value); setHasChanges(true); }}
                />
                <Select
                  label="Account type"
                  value={accountType}
                  onChange={(v) => { setAccountType(v); setHasChanges(true); }}
                  data={[
                    { value: 'checking', label: 'Checking' },
                    { value: 'savings', label: 'Savings' },
                  ]}
                />
                <TextInput
                  label="Routing number"
                  placeholder="9-digit routing number"
                  value={routingNumber}
                  onChange={(e) => { setRoutingNumber(e.currentTarget.value); setHasChanges(true); }}
                  maxLength={9}
                />
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Account number"
                      placeholder="Account number"
                      value={accountNumber}
                      onChange={(e) => { setAccountNumber(e.currentTarget.value); setHasChanges(true); }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Confirm account number"
                      placeholder="Re-enter account number"
                      value={confirmAccountNumber}
                      onChange={(e) => { setConfirmAccountNumber(e.currentTarget.value); setHasChanges(true); }}
                      error={confirmAccountNumber && confirmAccountNumber !== accountNumber ? 'Account numbers do not match' : undefined}
                    />
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>

            <Card withBorder mt="lg">
              <Stack gap="lg">
                <div>
                  <Title order={4}>Rates</Title>
                  <Text size="sm" c="gray.6" mt="xs">
                    Standard rates configured for this practice
                  </Text>
                </div>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="Hourly rate for pre-licensed"
                      value="$75.00"
                      readOnly
                      styles={{ input: { backgroundColor: 'var(--mantine-color-gray-1)', cursor: 'default' } }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="Hourly rate for licensed"
                      value="$120.00"
                      readOnly
                      styles={{ input: { backgroundColor: 'var(--mantine-color-gray-1)', cursor: 'default' } }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="Monthly fee"
                      value="$500.00"
                      readOnly
                      styles={{ input: { backgroundColor: 'var(--mantine-color-gray-1)', cursor: 'default' } }}
                    />
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Delete Dialogs */}
        <DeleteConfirmationDialog
          isOpen={showDeleteLocationDialog}
          onClose={() => {
            setShowDeleteLocationDialog(false);
            setLocationToDelete(null);
          }}
          onConfirm={() => {
            void (async () => {
              if (locationToDelete) {
                try {
                  await locationResources.deleteSite(locationToDelete);
                } catch (err) {
                  notifications.show({
                    title: 'Could not delete location',
                    message: getOperationOutcomeMessage(err),
                    color: 'red',
                  });
                }
              }
              setShowDeleteLocationDialog(false);
              setLocationToDelete(null);
            })();
          }}
          title="Delete Location"
          description="Are you sure you want to delete this location?"
          confirmText="Delete Location"
        />

        <DeleteConfirmationDialog
          isOpen={showDeleteDocumentDialog}
          onClose={() => {
            setShowDeleteDocumentDialog(false);
            setDocumentToDelete(null);
          }}
          onConfirm={() => {
            void (async () => {
              if (documentToDelete) {
                try {
                  await deleteLegalDocument(medplum, practice.id, documentToDelete);
                  setLegalDocuments((prev) => prev.filter((d) => d.id !== documentToDelete));
                  notifications.show({ title: 'Document deleted', message: 'Legal document removed.', color: 'green' });
                } catch (err) {
                  const msg = err instanceof Error ? err.message : String(err);
                  notifications.show({ title: 'Could not delete document', message: msg, color: 'red' });
                }
              }
              setShowDeleteDocumentDialog(false);
              setDocumentToDelete(null);
            })();
          }}
          title="Delete Document"
          description="Are you sure you want to delete this document?"
          confirmText="Delete Document"
        />
      </Stack>
    </Container>
  );
}

